<template>
  <div class="accounts-content-list scrollbar-default">
    <div class="accounts-content-list__top">
      <div class="accounts-content-list__info">
        <h3 class="accounts-content-list__title">
          {{ title }}
        </h3>

        <span class="accounts-content-list__count">{{ filteredContent.length }}</span>
      </div>
      <div class="accounts-content-list__search">
        <ContentSearch
          :content="content"
          :keep-open-on-search="true"
          @search="onSearch" />
      </div>
    </div>

    <div
      class="accounts-content-list__container"
      :class="{'accounts-content-list__container--text': isTextView}">
      <div
        v-for="(image, index) in visibleContent"
        :key="index"
        class="accounts-content-list__item">
        <BaseTooltip
          v-if="image.additional_value"
          :key="`image-${image.id}`"
          class="accounts-content-list__content-title">
          <template #default>
            <img
              v-if="image.additional_value"
              :src="image.additional_value"
              :alt="image.value"
              class="accounts-content-list__image lazy-load">
          </template>

          <template #content>
            {{ image.value }}
          </template>
        </BaseTooltip>

        <div
          v-else
          class="accounts-content-list__text-item">
          {{ image.value }}
        </div>

        <BaseTooltip
          v-if="image.additional_value"
          :key="`title-${image.id}`"
          class="accounts-content-list__content-title">
          <template #default>
            {{ image.value }}
          </template>

          <template #content>
            {{ image.value }}
          </template>
        </BaseTooltip>
      </div>
    </div>

    <div
      v-if="loading"
      class="accounts-content-list__loading">
      Loading...
    </div>
  </div>
</template>

<script>
import ContentSearch from 'Root/pages/game/components/ContentSearch.vue';
import BaseTooltip from 'Components/ui/Tooltip/BaseTooltip.vue';

export default {
  name: 'AccountContentList',

  components: {
    ContentSearch,
    BaseTooltip,
  },

  props: {
    title: {
      type: String,
      required: true,
    },

    content: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      filteredContent: [],
      visibleContent: [],
      itemsPerPage: 60,
      currentPage: 1,
      loading: false,
      observer: null,
    };
  },

  computed: {
    isTextView() {
      return !this.content.some((item) => item.additional_value);
    },
  },

  mounted() {
    this.filteredContent = this.content;
    this.initIntersectionObserver();
    this.updateVisibleContent();
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  methods: {
    initIntersectionObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          const lastEntry = entries[entries.length - 1];
          if (lastEntry.isIntersecting) {
            this.loadMoreContent();
          }
        },
        { threshold: 0.1, root: this.$el },
      );
    },

    onSearch(searchTerm) {
      if (searchTerm === '') {
        this.filteredContent = this.content;
      } else {
        const normalizedSearchTerm = searchTerm.toLowerCase();
        this.filteredContent = this.content.filter((item) => item.value.toLowerCase().includes(normalizedSearchTerm));
      }
      this.resetLazyLoading();
    },

    updateVisibleContent() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = this.currentPage * this.itemsPerPage;
      const newItems = this.filteredContent.slice(start, end);
      this.visibleContent = [...this.visibleContent, ...newItems];

      this.$nextTick(() => {
        const items = this.$el.querySelectorAll('.accounts-content-list__item');
        if (items.length > 0) {
          this.observer.observe(items[items.length - 1]);
        }
      });
    },

    loadMoreContent() {
      if (this.loading || this.visibleContent.length >= this.filteredContent.length) {
        return;
      }

      this.loading = true;
      setTimeout(() => {
        this.currentPage += 1;
        this.updateVisibleContent();
        this.loading = false;
      }, 1000);
    },

    resetLazyLoading() {
      this.visibleContent = [];
      this.currentPage = 1;
      this.updateVisibleContent();
    },
  },
};
</script>

<style scoped lang="scss">
.accounts-content-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px 24px;
  max-height: 420px;
  border: 1px solid rgba($color--basic-menu, 0.8);
  border-radius: 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.accounts-content-list__top {
  display: flex;
  justify-content: space-between;
  position: sticky;
  top: 0;
  align-items: center;
  padding: 24px 0;
  background-color: $color--basic-black;
}

.accounts-content-list__info {
  display: flex;
  align-items: center;
}

.accounts-content-list__title {
  font-size: 19px;
  font-weight: 500;
  margin-right: 10px;
}

.accounts-content-list__content-title {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 68px;
  cursor: pointer;
  margin-top: auto;
}

.accounts-content-list__count {
  font-size: 19px;
  font-weight: 500;
  color: $color--basic-superdark-gray;
}

.accounts-content-list__search {
  position: relative;
}

.accounts-content-list__container {
  display: grid;
  grid-template-columns: repeat(auto-fill, 69px);
  gap: 16px;
  justify-content: space-between;
}

.accounts-content-list__container--text {
  grid-template-columns: repeat(4, 1fr);
}

.accounts-content-list__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 69px;
}

.accounts-content-list__image {
  width: 100%;
  border-radius: 10px;
  margin-bottom: 8px;
  flex-grow: 1;
  object-fit: contain;
}

.accounts-content-list__text-item {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 16px;
  border: 1px solid rgba($color--basic-menu, 0.8);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: $color--basic-white;
  width: 100%;
}

.accounts-content-list__loading {
  text-align: center;
  padding: 16px;
  font-size: 16px;
  color: $color--basic-superdark-gray;
}
</style>
